import { useState, useEffect, useCallback, useMemo } from 'react';
import useAuth from '../../hooks/utils/useAuth';
import { useResourcesByTopic } from '../../hooks/api/useBrowseResources';
import { useSessionStorage } from '../../hooks/utils/useSessionStorage';
import { useTagGroups } from '../../hooks/api/useTagGroups';
import { useBrowseFilters, ResourceTypeFilter } from '../../hooks/utils/useBrowseFilters';
import { useProviderPlan } from '../../hooks/api/useExternalProviderPlans';
import { useProviderResources } from '../../hooks/utils/useProviderResources';
import { ResourceTile } from '../../components/resource-tile';
import { DropdownPill } from '../../components/dropdown-pill';
import { SearchInput } from '../../components/search-input';
import { Header } from '../../components/layout/header';
import { Footer } from '../../components/layout/footer';
import { ContentSection } from '../../components/content-section';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';
import { TopicFilterDropdown } from '../../components/topic-filter-dropdown';
import { Features, useFeatureFlag } from '../../hooks/api/useFeatureFlag';
import { MatchStyleTrialDropdown } from '../../components/match-style-trial-dropdown';
import { useMatchStyleTrial } from '../../components/peer-listener-match-hint';

const BrowseResources = () => {
  const { user } = useAuth();
  const clientId = useSessionStorage('clientId') || user?.caller_role?.active_subscription?.package?.client?.id;
  const clientName = user?.caller_role?.active_subscription?.package?.client?.name;
  const matchPreviewEnabled = useFeatureFlag(Features.SEARCH_MATCH_EXPLAINABILITY).enabled;
  const { matchStyle, setMatchStyle } = useMatchStyleTrial(matchPreviewEnabled);

  const {
    selectedTagGroupId,
    setSelectedTagGroupId,
    selectedTagIds,
    searchQuery,
    setSearchQuery,
    selectedResourceType,
    setSelectedResourceType,
  } = useBrowseFilters(false, true, true);

  const { getTagGroupById } = useTagGroups(true);
  const { data: topicTagGroups } = useTagGroups();
  const { providerPlan, isLoading: providerPlansLoading } = useProviderPlan();
  const providerResources = useProviderResources();
  const resourceTypeFilter = selectedResourceType;
  const setResourceTypeFilter = setSelectedResourceType!;

  const ITEMS_PER_PAGE = resourceTypeFilter === undefined ? 4 : 12;
  const ITEMS_TO_LOAD_MORE = 4;
  const [clientItemsShown, setClientItemsShown] = useState(ITEMS_PER_PAGE);
  const [globalItemsShown, setGlobalItemsShown] = useState(ITEMS_PER_PAGE);
  const [providerItemsShown, setProviderItemsShown] = useState(ITEMS_PER_PAGE);

  const {
    data: resourcesData,
    isLoading: isLoadingResources,
    error: resourcesError,
  } = useResourcesByTopic(
    selectedTagGroupId,
    Number(clientId),
    selectedTagIds.length > 0 ? selectedTagIds : undefined,
    searchQuery && searchQuery.trim().length > 0 ? searchQuery : undefined,
  );

  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery?.(query);
    },
    [setSearchQuery],
  );

  // Split resources into client and global
  const { clientResources, globalResources } = useMemo(() => {
    if (!resourcesData?.data) {
      return { clientResources: [], globalResources: [] };
    }
    const client = resourcesData.data.filter((resource) => resource.client_id !== null);
    const global = resourcesData.data.filter((resource) => resource.client_id === null);
    return { clientResources: client, globalResources: global };
  }, [resourcesData?.data]);

  // Reset itemsShown when filters or search change
  useEffect(() => {
    setClientItemsShown(ITEMS_PER_PAGE);
    setGlobalItemsShown(ITEMS_PER_PAGE);
    setProviderItemsShown(ITEMS_PER_PAGE);
  }, [selectedTagIds, selectedTagGroupId, searchQuery, resourceTypeFilter, ITEMS_PER_PAGE]);

  const loadMoreClientItems = () => {
    setClientItemsShown((prev) => Math.min(prev + ITEMS_TO_LOAD_MORE, clientResources.length));
  };

  const loadMoreGlobalItems = () => {
    setGlobalItemsShown((prev) => Math.min(prev + ITEMS_TO_LOAD_MORE, globalResources.length));
  };

  const loadMoreProviderItems = () => {
    setProviderItemsShown((prev) => Math.min(prev + ITEMS_TO_LOAD_MORE, providerResources.length));
  };

  const dfMdMedia = useMediaQuery('md');
  const hasSearch = Boolean(searchQuery?.trim());
  const activeMatchStyle = hasSearch ? matchStyle : 'none';

  const dropdownOptions = useMemo(
    () =>
      [
        providerResources.length > 0 && { value: 'licensed_care', label: 'Licensed Care' },
        clientResources.length > 0 && { value: 'client', label: `${clientName}: Resources` },
        globalResources.length > 0 && { value: 'national', label: 'National Resources' },
      ].filter(Boolean) as { value: string; label: string }[],
    [providerResources, clientResources, globalResources, clientName],
  );

  if (!clientId) {
    return (
      <div className="flex min-h-screen flex-col bg-white">
        <Header />
        <div className={`flex-1 ${!dfMdMedia ? 'pt-[76px]' : ''}`}>
          <div className="mx-auto max-w-[1280px] px-6 py-8">
            <h1 className="font-open-sans mb-2 text-3xl font-semibold leading-[60px] text-black">Resources</h1>
            <p>Loading...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header />
      <div className={`flex-1 ${!dfMdMedia ? 'pt-[76px]' : ''}`}>
        <div className="max-w-page mx-auto px-6 py-8">
          <div className="mb-6">
            <h1 className="font-albert-sans text-30px mb-6 font-semibold text-black">Resources</h1>
            <div className={`mb-6 flex items-start justify-start gap-3 ${dfMdMedia ? 'flex-row' : 'flex-col'}`}>
              <SearchInput onSearch={handleSearch} initialValue={searchQuery || ''} className="mb-0" />
              <DropdownPill
                label="Resource type"
                placeholder="Resource type"
                options={dropdownOptions}
                selectedValue={resourceTypeFilter}
                onSelect={(value) => setResourceTypeFilter((value as ResourceTypeFilter) || undefined)}
              />
              {resourceTypeFilter !== 'licensed_care' && (
                <TopicFilterDropdown
                  selectedTagGroupId={selectedTagGroupId}
                  onSelect={(value) => setSelectedTagGroupId(value as number | undefined)}
                  topicTagGroups={topicTagGroups}
                />
              )}
              {matchPreviewEnabled ? (
                <MatchStyleTrialDropdown selectedValue={matchStyle} onSelect={setMatchStyle} />
              ) : null}
            </div>
            {matchPreviewEnabled ? (
              <p className="font-open-sans mb-6 text-sm text-gray-500">
                Match styles are a preview for your client. Search to see why a resource matched.
              </p>
            ) : null}
          </div>

          {/* Resources Display */}
          {resourcesError ? (
            <div className="text-center text-red-500">Error loading resources. Please try again.</div>
          ) : (
            <div className="space-y-12">
              {/* Provider Plans Section - only show when all types or licensed_care selected */}
              {(providerPlan || providerPlansLoading) && (providerResources.length > 0 || providerPlansLoading) && (
                <ContentSection
                  title="Licensed Care"
                  items={providerResources}
                  itemsShown={providerItemsShown}
                  onLoadMore={loadMoreProviderItems}
                  shouldShow={resourceTypeFilter === undefined || resourceTypeFilter === 'licensed_care'}
                  isLoading={providerPlansLoading}
                  renderItem={(resource, index) => {
                    return (
                      <ResourceTile
                        key={resource?.name || `skeleton-${index}`}
                        image={resource?.image_url}
                        resourceName={resource?.name}
                        caption={resource?.description}
                        tagGroupNames={resource?.tag_names}
                        providerResource={resource}
                        isLoading={!resource}
                      />
                    );
                  }}
                />
              )}

              {/* Client Resources Section */}
              <ContentSection
                title={`${clientName}: Resources`}
                items={clientResources}
                itemsShown={clientItemsShown}
                onLoadMore={loadMoreClientItems}
                shouldShow={
                  (clientResources.length > 0 || isLoadingResources) &&
                  (resourceTypeFilter === undefined || resourceTypeFilter === 'client')
                }
                isLoading={isLoadingResources}
                renderItem={(resource, index) => {
                  const selectedTagGroup = selectedTagGroupId ? getTagGroupById(selectedTagGroupId) : undefined;
                  return (
                    <ResourceTile
                      key={resource?.id || `skeleton-${index}`}
                      resource={resource || undefined}
                      id={resource?.id}
                      image={resource?.image_url}
                      resourceName={resource?.name}
                      caption={resource?.description}
                      isGlobal={resource ? false : undefined}
                      tagGroupNames={resource?.tag_group_names}
                      selectedTagGroupName={selectedTagGroup?.name}
                      isLoading={!resource}
                      matchStyle={activeMatchStyle}
                    />
                  );
                }}
              />

              {/* Global Resources Section */}
              <ContentSection
                title="National Resources"
                items={globalResources}
                itemsShown={globalItemsShown}
                onLoadMore={loadMoreGlobalItems}
                shouldShow={
                  (globalResources.length > 0 || isLoadingResources) &&
                  (resourceTypeFilter === undefined || resourceTypeFilter === 'national')
                }
                isLoading={isLoadingResources}
                renderItem={(resource, index) => {
                  const selectedTagGroup = selectedTagGroupId ? getTagGroupById(selectedTagGroupId) : undefined;
                  return (
                    <ResourceTile
                      key={resource?.id || `skeleton-${index}`}
                      resource={resource || undefined}
                      id={resource?.id}
                      image={resource?.image_url}
                      resourceName={resource?.name}
                      caption={resource?.description}
                      isGlobal={resource ? true : undefined}
                      tagGroupNames={resource?.tag_group_names}
                      selectedTagGroupName={selectedTagGroup?.name}
                      isLoading={!resource}
                      matchStyle={activeMatchStyle}
                    />
                  );
                }}
              />

              {/* No resources message */}
              {!isLoadingResources &&
                !providerPlansLoading &&
                ((resourceTypeFilter === 'client' && clientResources.length === 0) ||
                  (resourceTypeFilter === 'national' && globalResources.length === 0) ||
                  (resourceTypeFilter === 'licensed_care' && providerResources.length === 0) ||
                  (resourceTypeFilter === undefined &&
                    resourcesData?.count === 0 &&
                    providerResources.length === 0)) && (
                  <div className="text-center text-gray-500">
                    <p className="text-lg">No resources found.</p>
                    {(selectedTagGroupId ||
                      selectedTagIds.length > 0 ||
                      resourceTypeFilter !== undefined ||
                      (searchQuery && searchQuery.trim().length > 0)) && (
                      <p className="mt-2 text-sm">Try adjusting your filters or search query.</p>
                    )}
                  </div>
                )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default BrowseResources;
